import { lazy, Suspense } from "react"
import { createBrowserRouter } from "react-router-dom"
import { AuthLayout } from "@/components/layouts/auth-layout"
import { AppLayout } from "@/components/layouts/app-layout"
import { SettingsLayout } from "@/components/layouts/settings-layout"
import { RequireAuth } from "@/components/require-auth"
import { LoginPage } from "@/pages/auth/login-page"
import { RegisterPage } from "@/pages/auth/register-page"
import { ForgotPasswordPage } from "@/pages/auth/forgot-password-page"
import { ResetPasswordPage } from "@/pages/auth/reset-password-page"
import { MfaVerifyPage } from "@/pages/auth/mfa-verify-page"
import { DashboardPage } from "@/pages/dashboard/dashboard-page"
import { CompaniesListPage } from "@/pages/companies/companies-list-page"
import { CompanyDetailPage } from "@/pages/companies/company-detail-page"
import { CompanyFormPage } from "@/pages/companies/company-form-page"
import { ContactsListPage } from "@/pages/contacts/contacts-list-page"
import { ContactDetailPage } from "@/pages/contacts/contact-detail-page"
import { ContactFormPage } from "@/pages/contacts/contact-form-page"
import { ProductsListPage } from "@/pages/products/products-list-page"
import { ProductDetailPage } from "@/pages/products/product-detail-page"
import { ProductFormPage } from "@/pages/products/product-form-page"
import { DealPhasesPage } from "@/pages/settings/deal-phases-page"
import { QuoteTemplatesPage } from "@/pages/settings/quote-templates-page"
import { DealsListPage } from "@/pages/deals/deals-list-page"
import { DealDetailPage } from "@/pages/deals/deal-detail-page"
import { DealFormPage } from "@/pages/deals/deal-form-page"
import { DealsBoardPage } from "@/pages/deals/deals-board-page"
import { TasksListPage } from "@/pages/tasks/tasks-list-page"
import { TaskDetailPage } from "@/pages/tasks/task-detail-page"
import { TaskFormPage } from "@/pages/tasks/task-form-page"
import { CalendarPage } from "@/pages/calendar/calendar-page"
import { DocumentsPage } from "@/pages/documents/documents-page"
import { InvoicesListPage } from "@/pages/invoices/invoices-list-page"
import { InvoiceDetailPage } from "@/pages/invoices/invoice-detail-page"
import { InvoiceFormPage } from "@/pages/invoices/invoice-form-page"
import { QuotesListPage } from "@/pages/quotes/quotes-list-page"
import { QuoteDetailPage } from "@/pages/quotes/quote-detail-page"
import { QuoteFormPage } from "@/pages/quotes/quote-form-page"
import { PublicQuotePage } from "@/pages/quotes/public-quote-page"
import { EmailAccountsPage } from "@/pages/settings/email-accounts-page"
import { EmailTemplatesPage } from "@/pages/settings/email-templates-page"
import { EmailInboxPage } from "@/pages/mail/email-inbox-page"
import { SmsPage } from "@/pages/sms/sms-page"
import { ServiceTicketsListPage } from "@/pages/service/service-tickets-list-page"
import { ServiceTicketDetailPage } from "@/pages/service/service-ticket-detail-page"
import { ServiceTicketFormPage } from "@/pages/service/service-ticket-form-page"
import { ServiceTicketsBoardPage } from "@/pages/service/service-tickets-board-page"
import { TicketPhasesPage } from "@/pages/settings/ticket-phases-page"
import { EquipmentListPage } from "@/pages/service/equipment-list-page"
import { EquipmentDetailPage } from "@/pages/service/equipment-detail-page"
import { EquipmentFormPage } from "@/pages/service/equipment-form-page"
import { ReportsPage } from "@/pages/reports/reports-page"
import { GoalsListPage } from "@/pages/goals/goals-list-page"
import { GoalDetailPage } from "@/pages/goals/goal-detail-page"
import { GoalFormPage } from "@/pages/goals/goal-form-page"
import { TrainingListPage } from "@/pages/training/training-list-page"
import { TrainingDetailPage } from "@/pages/training/training-detail-page"
import { TrainingFormPage } from "@/pages/training/training-form-page"
import { WorkflowsListPage } from "@/pages/workflows/workflows-list-page"
import { WorkflowDetailPage } from "@/pages/workflows/workflow-detail-page"
import { WebhooksListPage } from "@/pages/webhooks/webhooks-list-page"
import { WebhookDetailPage } from "@/pages/webhooks/webhook-detail-page"
import { WebhookFormPage } from "@/pages/webhooks/webhook-form-page"
import { IntegrationsPage } from "@/pages/settings/integrations-page"
import { TaskCategoriesPage } from "@/pages/settings/task-categories-page"
import { TaskTemplatesPage } from "@/pages/settings/task-templates-page"
import { AiChatPage } from "@/pages/ai/ai-chat-page"

// Settings placeholder pages
import { SettingsIndexPage } from "@/pages/settings/settings-index-page"
import { CompanyProfilePage } from "@/pages/settings/company-profile-page"
import { MyProfilePage } from "@/pages/settings/my-profile-page"
import { DictionariesPage } from "@/pages/settings/dictionaries-page"
import { UsersSettingsPage } from "@/pages/settings/users-page"
import { GroupsSettingsPage } from "@/pages/settings/groups-page"
import { PermissionsSettingsPage } from "@/pages/settings/permissions-page"
import { CustomFieldsPage } from "@/pages/settings/custom-fields-page"
import { DataImportPage } from "@/pages/settings/data-import-page"
import { DataExportPage } from "@/pages/settings/data-export-page"
import { SmsSettingsPage } from "@/pages/settings/sms-settings-page"
import { NotificationsSettingsPage } from "@/pages/settings/notifications-page"
import { WebhooksSettingsPage } from "@/pages/settings/webhooks-settings-page"

// Lazy-loaded heavy/less-frequently-used pages for code splitting
const CustomReportBuilderPage = lazy(() => import("@/pages/reports/custom-report-builder-page").then(m => ({ default: m.CustomReportBuilderPage })))
const WorkflowBuilderPage = lazy(() => import("@/pages/workflows/workflow-builder-page").then(m => ({ default: m.WorkflowBuilderPage })))
const QuoteTemplateBuilderPage = lazy(() => import("@/pages/settings/quote-template-builder-page").then(m => ({ default: m.QuoteTemplateBuilderPage })))
const CalendarSyncPage = lazy(() => import("@/pages/settings/calendar-sync-page").then(m => ({ default: m.CalendarSyncPage })))
const OauthApplicationsPage = lazy(() => import("@/pages/settings/oauth-applications-page"))

export const router = createBrowserRouter([
  // Public routes (no auth required)
  { path: "/quotes/view/:token", element: <PublicQuotePage /> },
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      { path: "/mfa-verify", element: <MfaVerifyPage /> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { path: "/ai", element: <AiChatPage /> },
      { path: "/ai/:conversationId", element: <AiChatPage /> },
      { path: "/dashboard", element: <DashboardPage /> },
      { path: "/companies", element: <CompaniesListPage /> },
      { path: "/companies/new", element: <CompanyFormPage /> },
      { path: "/companies/:id", element: <CompanyDetailPage /> },
      { path: "/companies/:id/edit", element: <CompanyFormPage /> },
      { path: "/contacts", element: <ContactsListPage /> },
      { path: "/contacts/new", element: <ContactFormPage /> },
      { path: "/contacts/:id", element: <ContactDetailPage /> },
      { path: "/contacts/:id/edit", element: <ContactFormPage /> },
      { path: "/products", element: <ProductsListPage /> },
      { path: "/products/new", element: <ProductFormPage /> },
      { path: "/products/:id", element: <ProductDetailPage /> },
      { path: "/products/:id/edit", element: <ProductFormPage /> },
      { path: "/deals", element: <DealsListPage /> },
      { path: "/deals/board", element: <DealsBoardPage /> },
      { path: "/deals/new", element: <DealFormPage /> },
      { path: "/deals/:id", element: <DealDetailPage /> },
      { path: "/deals/:id/edit", element: <DealFormPage /> },
      { path: "/tasks", element: <TasksListPage /> },
      { path: "/tasks/new", element: <TaskFormPage /> },
      { path: "/tasks/:id", element: <TaskDetailPage /> },
      { path: "/tasks/:id/edit", element: <TaskFormPage /> },
      { path: "/calendar", element: <CalendarPage /> },
      { path: "/documents", element: <DocumentsPage /> },
      { path: "/invoices", element: <InvoicesListPage /> },
      { path: "/invoices/new", element: <InvoiceFormPage /> },
      { path: "/invoices/:id", element: <InvoiceDetailPage /> },
      { path: "/invoices/:id/edit", element: <InvoiceFormPage /> },
      { path: "/quotes", element: <QuotesListPage /> },
      { path: "/quotes/new", element: <QuoteFormPage /> },
      { path: "/quotes/:id", element: <QuoteDetailPage /> },
      { path: "/quotes/:id/edit", element: <QuoteFormPage /> },
      { path: "/mail", element: <EmailInboxPage /> },
      { path: "/sms", element: <SmsPage /> },
      { path: "/service", element: <ServiceTicketsListPage /> },
      { path: "/service/board", element: <ServiceTicketsBoardPage /> },
      { path: "/service/new", element: <ServiceTicketFormPage /> },
      { path: "/service/:id", element: <ServiceTicketDetailPage /> },
      { path: "/service/:id/edit", element: <ServiceTicketFormPage /> },
      { path: "/equipment", element: <EquipmentListPage /> },
      { path: "/equipment/new", element: <EquipmentFormPage /> },
      { path: "/equipment/:id", element: <EquipmentDetailPage /> },
      { path: "/equipment/:id/edit", element: <EquipmentFormPage /> },

      { path: "/reports", element: <ReportsPage /> },
      { path: "/reports/custom", element: <Suspense fallback={<div />}><CustomReportBuilderPage /></Suspense> },
      { path: "/goals", element: <GoalsListPage /> },
      { path: "/goals/new", element: <GoalFormPage /> },
      { path: "/goals/:id", element: <GoalDetailPage /> },
      { path: "/goals/:id/edit", element: <GoalFormPage /> },
      { path: "/training", element: <TrainingListPage /> },
      { path: "/training/new", element: <TrainingFormPage /> },
      { path: "/training/:id", element: <TrainingDetailPage /> },
      { path: "/training/:id/edit", element: <TrainingFormPage /> },
      { path: "/automations", element: <WorkflowsListPage /> },
      { path: "/automations/new", element: <Suspense fallback={<div />}><WorkflowBuilderPage /></Suspense> },
      { path: "/automations/:id", element: <WorkflowDetailPage /> },
      { path: "/automations/:id/edit", element: <Suspense fallback={<div />}><WorkflowBuilderPage /></Suspense> },
      { path: "/webhooks", element: <WebhooksListPage /> },
      { path: "/webhooks/new", element: <WebhookFormPage /> },
      { path: "/webhooks/:id", element: <WebhookDetailPage /> },
      { path: "/webhooks/:id/edit", element: <WebhookFormPage /> },

      // ── Settings (nested under SettingsLayout) ───────────────
      {
        path: "/settings",
        element: <SettingsLayout />,
        children: [
          { index: true, element: <SettingsIndexPage /> },
          // Ogolne
          { path: "company", element: <CompanyProfilePage /> },
          { path: "profile", element: <MyProfilePage /> },
          // Sprzedaz
          { path: "deal-phases", element: <DealPhasesPage /> },
          { path: "ticket-phases", element: <TicketPhasesPage /> },
          { path: "quote-templates", element: <QuoteTemplatesPage /> },
          { path: "quote-templates/:id", element: <Suspense fallback={<div />}><QuoteTemplateBuilderPage /></Suspense> },
          { path: "dictionaries", element: <DictionariesPage /> },
          // Zadania
          { path: "task-categories", element: <TaskCategoriesPage /> },
          { path: "task-templates", element: <TaskTemplatesPage /> },
          // Zespol
          { path: "users", element: <UsersSettingsPage /> },
          { path: "groups", element: <GroupsSettingsPage /> },
          { path: "permissions", element: <PermissionsSettingsPage /> },
          // Dane
          { path: "custom-fields", element: <CustomFieldsPage /> },
          { path: "import", element: <DataImportPage /> },
          { path: "export", element: <DataExportPage /> },
          // Komunikacja
          { path: "email-accounts", element: <EmailAccountsPage /> },
          { path: "email-templates", element: <EmailTemplatesPage /> },
          { path: "sms-settings", element: <SmsSettingsPage /> },
          { path: "notifications", element: <NotificationsSettingsPage /> },
          // Integracje
          { path: "integrations", element: <IntegrationsPage /> },
          { path: "calendar-sync", element: <Suspense fallback={<div />}><CalendarSyncPage /></Suspense> },
          { path: "webhooks", element: <WebhooksSettingsPage /> },
          { path: "oauth", element: <Suspense fallback={<div />}><OauthApplicationsPage /></Suspense> },
        ],
      },

      { path: "/", element: <DashboardPage /> },
    ],
  },
])
